import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Sync as SyncIcon,
  Refresh as RefreshIcon,
  CallMerge as MergeIcon
} from '@mui/icons-material';
import { authGet, authPost, authPut, authDelete } from '../../services/apiService';
import TeamRecordsDialog from './TeamRecordsDialog';

const EMPTY_FORM = {
  id: '',
  displayName: '',
  description: '',
  enabled: false,
  stream: '',
  adoName: '',
  previousAdoNames: []
};

const TeamManagement = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  // { mode: 'merge' | 'retag', team } while the records dialog is open
  const [records, setRecords] = useState(null);

  useEffect(() => {
    loadTeams();
  }, []);

  const loadTeams = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await authGet('/api/admin/teams');
      
      if (data.success) {
        setTeams(data.data);
      } else {
        setError(data.message || 'Failed to load teams');
      }
    } catch (err) {
      console.error('Error loading teams:', err);
      setError('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const syncTeamsFromSprint = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const data = await authPost('/api/admin/teams/sync-from-sprint', {});
      
      if (data.success) {
        setSuccess(data.message);
        loadTeams(); // Reload teams after sync
      } else {
        setError(data.message || 'Failed to sync teams');
      }
    } catch (err) {
      console.error('Error syncing teams:', err);
      setError('Failed to sync teams from sprint data');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (teamId, currentEnabled) => {
    try {
      const data = await authPut(`/api/admin/teams/${teamId}/toggle`, {});
      
      if (data.success) {
        setSuccess(`Team ${data.data.enabled ? 'enabled' : 'disabled'} successfully`);
        loadTeams(); // Reload teams
      } else {
        setError(data.message || 'Failed to toggle team status');
      }
    } catch (err) {
      console.error('Error toggling team:', err);
      setError('Failed to toggle team status');
    }
  };

  const handleOpenDialog = (team = null) => {
    if (team) {
      setEditingTeam(team);
      setFormData({
        id: team.id,
        displayName: team.displayName,
        description: team.description || '',
        enabled: team.enabled,
        stream: team.stream || '',
        adoName: team.adoName || '',
        previousAdoNames: team.previousAdoNames || []
      });
    } else {
      setEditingTeam(null);
      setFormData(EMPTY_FORM);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTeam(null);
    setFormData(EMPTY_FORM);
  };

  const handleSaveTeam = async () => {
    try {
      let data;
      if (editingTeam) {
        data = await authPut(`/api/admin/teams/${editingTeam.id}`, formData);
      } else {
        data = await authPost('/api/admin/teams', formData);
      }
      
      if (data.success) {
        setSuccess(data.message);
        handleCloseDialog();
        loadTeams();
      } else {
        setError(data.message || 'Failed to save team');
      }
    } catch (err) {
      console.error('Error saving team:', err);
      setError(err.message || 'Failed to save team');
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Are you sure you want to delete this team?')) {
      return;
    }
    
    try {
      const data = await authDelete(`/api/admin/teams/${teamId}`);
      
      if (data.success) {
        setSuccess('Team deleted successfully');
        loadTeams();
      } else {
        setError(data.message || 'Failed to delete team');
      }
    } catch (err) {
      console.error('Error deleting team:', err);
      setError('Failed to delete team');
    }
  };

  const handleRecordsDone = (result) => {
    const moved = (result?.totals?.records || 0) + (result?.totals?.nested || 0);
    const kept = result?.totals?.conflicts || 0;
    setSuccess(`${records?.mode === 'merge' ? 'Teams merged. ' : ''}${moved} record${moved === 1 ? '' : 's'} moved${kept ? `, ${kept} kept under the old name` : ''}.`);
    setRecords(null);
    loadTeams();
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Team Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadTeams}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<SyncIcon />}
            onClick={syncTeamsFromSprint}
            disabled={loading}
          >
            Sync from Sprint
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Team
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Getting Started:</strong> Click "Sync from Sprint" to discover teams from the latest sprint data. 
          Teams are initially disabled - enable them to make them available in the Sprint Report dashboard.
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          <strong>Team renamed in ADO?</strong> Edit the team, set its new ADO team name and keep the old one under
          previous names. Work items tagged with either name then count as this team. If the rename already created a
          second team here, use <MergeIcon fontSize="inherit" sx={{ verticalAlign: 'middle' }} /> Merge on the team you want to keep.
        </Typography>
      </Alert>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress />
        </Box>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Team ID</TableCell>
              <TableCell>Display Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Stream</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Enabled</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {teams.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No teams found. Click "Sync from Sprint" to discover teams from ADO.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              teams.map((team) => (
                <TableRow key={team.id}>
                  <TableCell>
                    {team.id}
                    {(team.adoName || team.previousAdoNames?.length > 0) && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        {team.adoName && <>ADO: {team.adoName}</>}
                        {team.adoName && team.previousAdoNames?.length > 0 && ' · '}
                        {team.previousAdoNames?.length > 0 && <>was {team.previousAdoNames.join(', ')}</>}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{team.displayName}</TableCell>
                  <TableCell>{team.description || '-'}</TableCell>
                  <TableCell>{team.stream || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={team.enabled ? 'Enabled' : 'Disabled'}
                      color={team.enabled ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={team.enabled}
                      onChange={() => handleToggleEnabled(team.id, team.enabled)}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Edit Team">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(team)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Merge another team into this one">
                        <IconButton
                          size="small"
                          aria-label={`Merge another team into ${team.displayName}`}
                          onClick={() => setRecords({ mode: 'merge', team })}
                        >
                          <MergeIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Team">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteTeam(team.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Team Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { minHeight: '400px' }
        }}
      >
        <DialogTitle>
          {editingTeam ? 'Edit Team' : 'Add New Team'}
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Team ID"
              value={formData.id}
              onChange={(e) => setFormData({ ...formData, id: e.target.value })}
              disabled={!!editingTeam}
              fullWidth
              required
              helperText={editingTeam
                ? "The portal's key for this team. It stays the same when the team is renamed in ADO."
                : 'Unique identifier for the team, usually its ADO team name (e.g., HQ-Core-SQ1)'}
            />
            <TextField
              label="ADO team name"
              value={formData.adoName}
              onChange={(e) => setFormData({ ...formData, adoName: e.target.value })}
              fullWidth
              placeholder={formData.id}
              helperText="The team's current name in ADO. Leave empty when it matches the Team ID."
            />
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={formData.previousAdoNames}
              onChange={(_e, value) => setFormData({ ...formData, previousAdoNames: value.map((v) => String(v).trim()).filter(Boolean) })}
              renderTags={(value, getTagProps) => value.map((option, index) => (
                <Chip size="small" label={option} {...getTagProps({ index })} key={option} />
              ))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Previous ADO names"
                  placeholder="Type a name and press Enter"
                  helperText="Old names from before a rename. Work items still tagged with them count as this team."
                />
              )}
            />
            {editingTeam && (editingTeam.adoName || editingTeam.previousAdoNames?.length > 0) && (
              <Box>
                <Button size="small" onClick={() => setRecords({ mode: 'retag', team: editingTeam })}>
                  Move records stored under old names…
                </Button>
              </Box>
            )}
            <TextField
              label="Display Name"
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              fullWidth
              required
              helperText="Human-readable team name"
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              helperText="Optional description of the team"
            />
            <FormControl fullWidth>
              <InputLabel>Stream</InputLabel>
              <Select
                value={formData.stream}
                label="Stream"
                onChange={(e) => setFormData({ ...formData, stream: e.target.value })}
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="Core">Core</MenuItem>
                <MenuItem value="PlatformCompliance">Platform & Compliance</MenuItem>
                <MenuItem value="CoCo for HighQ">CoCo for HighQ</MenuItem>
                <MenuItem value="CoCo Txm">CoCo Txm</MenuItem>
                <MenuItem value="AI-Experience">AI-Experience</MenuItem>
                {/* Add your new stream here - Example: */}
                {/* <MenuItem value="Your New Stream">Your New Stream</MenuItem> */}
              </Select>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                Select the stream this team belongs to (for Gantt chart organization)
              </Typography>
            </FormControl>
            <Box sx={{ mt: 1, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enabled}
                    onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                    color="primary"
                  />
                }
                label="Enable Team for Sprint Reports"
                sx={{ mb: 0 }}
              />
              <Typography variant="caption" color="textSecondary" display="block">
                {formData.enabled 
                  ? "Team will appear in Sprint Report dashboard dropdown" 
                  : "Team will be hidden from Sprint Report dashboard"
                }
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveTeam} 
            variant="contained"
            disabled={!formData.id || !formData.displayName}
          >
            {editingTeam ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <TeamRecordsDialog
        open={!!records}
        mode={records?.mode}
        team={records?.team}
        teams={teams}
        onClose={() => setRecords(null)}
        onDone={handleRecordsDone}
      />
    </Box>
  );
};

export default TeamManagement;
